import { useNavigate, useParams } from 'react-router-dom'
import styles from '../styles/containers.module.css'
import { Spinner } from '@verisart/shared'
import Claim, { AssetDetails } from '../components/organisms/Claim'
import useGetCertificate from '../lib/hooks/useGetCertificate'
import useClaim from '../lib/hooks/useClaim'
import t from '../lib/translation'
import { ComputedCertificateFeature } from '@verisart/shared'
import TopBar from 'src/components/organisms/TopBar'
import { useEffect, useState } from 'react'
import { useAccount } from 'wagmi'
import { useConnectWallet } from '@verisart/nft/src'
import MoveWalletDialog from 'src/components/organisms/MoveWalletDialog'

/**
 * Claiming "Custodial NFTs": nfts minting immediately at
 * purchase time and held in a custodial wallet. See comment in core under
 * `CustodialNFTService` for more details.
 */
const ClaimCustodialNFT = () => {
  const navigate = useNavigate()
  const { id } = useParams<'id'>()

  const {
    certificate,
    isLoading: isCertificateLoading,
    nft,
    withdrawalState,
  } = useGetCertificate(id as string)
  const assetDetails: AssetDetails | undefined = certificate
    ? {
        ...certificate,
        quantity: null,
        nftTestnet: certificate?.computedFeatures.includes(
          ComputedCertificateFeature.NFT_TESTNET
        ),
        signedMinting: false,
        claimed: false,
        tokenIds: certificate?.public.nft?.tokenId
          ? [certificate.public.nft.tokenId]
          : [],
        firstCertificatePrimaryAssetUrl: certificate?.files[0]?.url,
        completed: true,
        blockchain: certificate?.public.nft?.blockchain,
        contractAddress: certificate?.public.nft?.contractAddress,
        productionDating: certificate?.productionDating || null,
      }
    : undefined

  const { address: activeWallet, connector } = useAccount()
  const {
    connect,
    connectEmbedded: connectEmbeddedWallet,
    disconnect,
    showEmbedded,
    connecting,
  } = useConnectWallet()

  const {
    claim,
    claimed: claimedAfterCall,
    error: claimError,
    loading: claiming,
  } = useClaim(nft, activeWallet)

  const claimed = withdrawalState == 'COMPLETE' || claimedAfterCall

  const [showTopBar, setShowTopBar] = useState(true)

  const redirectRequiresWallet = t.claim.button.redirectLink.includes('$wallet')

  useEffect(() => {
    if (t.claim.dashboard.showTopBar === 'false') {
      setShowTopBar(false)
    }
  }, [])

  return (
    <div
      className={`ver-w-full ver-flex ver-flex-col ver-justify-center ver-mt-20 ${
        showTopBar ? '' : 'tablet:ver-mt-50'
      } ver-mb-20 ${styles.container}`}
    >
      {showTopBar && <TopBar blockchain={assetDetails?.blockchain} />}

      <div className="ver-flex ver-flex-grow ver-items-center ver-justify-center ver-px-4">
        {isCertificateLoading || !certificate ? (
          <Spinner />
        ) : (
          <Claim
            activeWallet={activeWallet}
            connector={connector?.id}
            assetDetails={assetDetails}
            claimed={claimed}
            loading={connecting || claiming}
            claimError={claimError}
            onBack={() => {
              const redirectLink = t.claim.button.redirectLink

              if (redirectLink === '/apps/verisart') {
                navigate('/')
              } else {
                window.open(
                  redirectLink
                    .replaceAll(
                      '$contractAddress',
                      assetDetails?.contractAddress || ''
                    )
                    .replaceAll('$tokenId', assetDetails?.tokenIds?.[0] ?? '')
                    .replaceAll('$blockchain', assetDetails?.blockchain || '')
                    .replaceAll('$wallet', activeWallet || ''),
                  '_blank'
                )
              }
            }}
            onConnect={connect}
            onConnectEmbedded={connectEmbeddedWallet}
            showEmbeddedWallet={showEmbedded}
            onClaim={claim}
            isCustodial={true}
            showTopBar={showTopBar}
            disconnect={disconnect}
            redirectRequiresWallet={redirectRequiresWallet}
          />
        )}
      </div>
      <MoveWalletDialog />
    </div>
  )
}

export default ClaimCustodialNFT
